<template>
  <div class="performance-dashboard" v-if="showDashboard">
    <div class="dashboard-header">
      <h3>🚀 性能监控面板</h3>
      <button @click="closeDashboard" class="close-btn">×</button>
    </div>
    
    <div class="dashboard-content">
      <!-- 基本性能指标 -->
      <div class="metrics-section">
        <h4>📊 性能指标</h4>
        <div class="metrics-grid">
          <div class="metric-card" v-if="performanceData.pageLoad">
            <div class="metric-label">页面加载时间</div>
            <div class="metric-value">{{ (performanceData.pageLoad.totalLoad / 1000).toFixed(2) }}s</div>
          </div>
          <div class="metric-card" v-if="performanceData.pageLoad">
            <div class="metric-label">首次内容绘制</div>
            <div class="metric-value">{{ (performanceData.pageLoad.fcp / 1000).toFixed(2) }}s</div>
          </div>
          <div class="metric-card" v-if="performanceData.memory">
            <div class="metric-label">内存使用</div>
            <div class="metric-value">{{ performanceData.memory.usage }}%</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">WebP支持</div>
            <div class="metric-value">{{ webpSupported ? '✅' : '❌' }}</div>
          </div>
        </div>
      </div>

      <!-- 图片预加载状态 -->
      <div class="preload-section">
        <h4>🖼️ 图片预加载</h4>
        <div class="preload-stats">
          <div class="stat-item">
            <span>已预加载: {{ imageStats.preloaded }}</span>
          </div>
          <div class="stat-item">
            <span>加载中: {{ imageStats.loading }}</span>
          </div>
        </div>
      </div>

      <!-- 路由预加载状态 -->
      <div class="route-section">
        <h4>🛣️ 路由预加载</h4>
        <div class="route-stats">
          <div class="stat-item">
            <span>已预加载: {{ routeStats.preloaded.length }} 个路由</span>
          </div>
          <div class="route-list" v-if="routeStats.preloaded.length > 0">
            <div class="route-item" v-for="route in routeStats.preloaded" :key="route">
              {{ route }}
            </div>
          </div>
        </div>
      </div>

      <!-- 缓存状态 -->
      <div class="cache-section">
        <h4>💾 缓存状态</h4>
        <div class="cache-stats">
          <div class="stat-item" v-if="cacheUsage">
            <span>存储使用: {{ formatBytes(cacheUsage.used) }} / {{ formatBytes(cacheUsage.available) }}</span>
          </div>
          <div class="cache-actions">
            <button @click="showCacheStats" class="action-btn">查看缓存统计</button>
            <button @click="clearCache" class="action-btn danger">清理缓存</button>
          </div>
        </div>
      </div>

      <!-- 优化建议 -->
      <div class="suggestions-section">
        <h4>💡 优化建议</h4>
        <div class="suggestions-list">
          <div 
            v-for="(suggestion, index) in suggestions" 
            :key="index"
            class="suggestion-item"
            :class="suggestion.priority"
          >
            <div class="suggestion-icon">
              {{ suggestion.priority === 'high' ? '🔴' : suggestion.priority === 'medium' ? '🟡' : '🔵' }}
            </div>
            <div class="suggestion-content">
              <div class="suggestion-title">{{ suggestion.type }}</div>
              <div class="suggestion-message">{{ suggestion.message }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PerformanceDashboard',
  data() {
    return {
      showDashboard: false,
      performanceData: {},
      imageStats: { preloaded: 0, loading: 0 },
      routeStats: { preloaded: [], preloading: [] },
      cacheUsage: null,
      suggestions: []
    }
  },
  computed: {
    webpSupported() {
      return this.$imagePreloader?.supportsWebp || false
    }
  },
  mounted() {
    // 监听键盘事件显示/隐藏面板
    document.addEventListener('keydown', this.handleKeyPress)
    
    // 定期更新数据
    this.updateInterval = setInterval(this.updateData, 5000)
    this.updateData()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyPress)
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    handleKeyPress(event) {
      // Ctrl + Shift + P 显示性能面板
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        event.preventDefault()
        this.toggleDashboard()
      }
    },
    
    toggleDashboard() {
      this.showDashboard = !this.showDashboard
      if (this.showDashboard) {
        this.updateData()
      }
    },
    
    closeDashboard() {
      this.showDashboard = false
    },
    
    async updateData() {
      if (!this.showDashboard) return
      
      try {
        // 获取性能监控数据
        if (this.$performanceMonitor) {
          this.performanceData = this.$performanceMonitor.metrics || {}
          this.suggestions = this.$performanceMonitor.getOptimizationSuggestions() || []
        }
        
        // 获取图片预加载统计
        if (this.$imagePreloader) {
          this.imageStats = this.$imagePreloader.getCacheStats() || { preloaded: 0, loading: 0 }
        }
        
        // 获取路由预加载统计
        if (this.$routePreloader) {
          this.routeStats = this.$routePreloader.getPreloadStatus() || { preloaded: [], preloading: [] }
        }
        
        // 获取缓存使用情况
        if (this.$sw) {
          this.cacheUsage = await this.$sw.getCacheUsage()
        }
      } catch (error) {
        console.error('更新性能数据失败:', error)
      }
    },
    
    async showCacheStats() {
      if (this.$sw) {
        const stats = await this.$sw.getCacheStats()
        console.group('📊 缓存统计')
        Object.entries(stats).forEach(([cacheName, count]) => {
          console.log(`${cacheName}: ${count} 个资源`)
        })
        console.groupEnd()
        
        if (this.$message) {
          this.$message.success('缓存统计已输出到控制台')
        }
      }
    },
    
    async clearCache() {
      if (this.$sw) {
        const success = await this.$sw.clearCache()
        if (this.$message) {
          this.$message({
            type: success ? 'success' : 'error',
            message: success ? '缓存清理成功' : '缓存清理失败'
          })
        }
        
        if (success) {
          this.updateData()
        }
      }
    },
    
    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style lang="scss" scoped>
.performance-dashboard {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 400px;
  max-height: 80vh;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 9999;
  overflow: hidden;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 18px;
    font-weight: bold;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: #333;
    }
  }
}

.dashboard-content {
  max-height: calc(80vh - 60px);
  overflow-y: auto;
  padding: 16px;
}

.metrics-section,
.preload-section,
.route-section,
.cache-section,
.suggestions-section {
  margin-bottom: 20px;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.metric-card {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 8px;
  text-align: center;
  
  .metric-label {
    font-size: 10px;
    color: #666;
    margin-bottom: 4px;
  }
  
  .metric-value {
    font-size: 14px;
    font-weight: bold;
    color: #333;
  }
}

.stat-item {
  padding: 4px 0;
  color: #333;
  font-size: 11px;
}

.route-list {
  margin-top: 8px;
}

.route-item {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 4px;
  font-size: 10px;
  color: #666;
}

.cache-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.action-btn {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 10px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #0056b3;
  }
  
  &.danger {
    background: #dc3545;
    
    &:hover {
      background: #c82333;
    }
  }
}

.suggestions-list {
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  border-left: 3px solid;
  
  &.high {
    border-left-color: #dc3545;
  }
  
  &.medium {
    border-left-color: #ffc107;
  }
  
  &.low {
    border-left-color: #17a2b8;
  }
}

.suggestion-icon {
  margin-right: 8px;
  font-size: 12px;
}

.suggestion-content {
  flex: 1;
  
  .suggestion-title {
    font-weight: 600;
    font-size: 11px;
    color: #333;
    margin-bottom: 2px;
  }
  
  .suggestion-message {
    font-size: 10px;
    color: #666;
    line-height: 1.4;
  }
}

// 滚动条样式
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}
</style>
